import dayjs from "dayjs";
import { formatInvoiceInr } from "../components/InvoiceFormShared";
import { getInvoiceById, getInvoicesByPatientId } from "../services/invoiceService";
import { getPatientById } from "../services/patientService";
import { getMedicineTransactionsByPatientId } from "../services/medicineTransactionService";
import { getLabTestById, getLabtestsList } from "../services/labtestService";
import { getIPDServices } from "../services/billingService";
import { getMedicineInventoryList } from "../services/medicineInventoryService";
import { getAllInventory } from "../services/inventoryService";
import {
  getAllSuppliers,
  getSupplierById,
} from "../services/supplierService";
import {
  getPathPabReportByPatient,
  getMergedPathLabReportsForPatient,
  getBilledTests,
} from "../services/pathLabTestReportService";
import { getPrescriptionsByPatientId } from "../services/prescriptionService";
import { getActiveSubscription } from "../services/subscriptionHistoryService";
import {
  getOrganizationId,
  getOrganizationName,
  fetchOrganizationProfile,
} from "./organizationUtils";
import {
  isExpenseInvoice,
  resolveInvoiceDisplayType,
  getInvoicePatientId,
} from "./invoiceTableUtils";
import { unwrapInvoiceRecord } from "./invoiceAttachmentUtils";
import {
  buildInvoiceDocumentHtml,
  buildOpdMedicineInvoiceDocumentHtml,
  buildPathologyInvoiceDocumentHtml,
  buildOpdServiceInvoiceDocumentHtml,
  printPatientSummaryDocument,
} from "./patientSummaryDocumentUtils";

const DEFAULT_NOTES =
  "This is a computer-generated invoice and does not require a signature.";

const GENERIC_PARTICULARS = new Set([
  "medicine",
  "pathologytest",
  "pathology",
  "registration",
  "consultation",
  "donation",
  "misc",
  "discharge",
  "homeopathy",
  "allopathy",
]);

const normalizeApiList = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  const candidates = [
    value.data,
    value.content,
    value.items,
    value.results,
    value.records,
    value.transactions,
    value.medicines,
    value.services,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
};

const hasText = (value) => {
  if (value == null || value === "") return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized !== "" && normalized !== "n/a" && normalized !== "na";
};

const resolveInvoiceNotes = (invoice) => {
  const particulars = String(
    invoice?.particulars ?? invoice?.comments ?? invoice?.remarks ?? ""
  ).trim();
  if (!particulars) return DEFAULT_NOTES;
  if (GENERIC_PARTICULARS.has(particulars.toLowerCase())) return DEFAULT_NOTES;
  return particulars;
};

const resolveVisitDate = (invoice) => {
  const raw = invoice?.date ?? invoice?.createdAt ?? invoice?.created_at;
  if (raw && dayjs(raw).isValid()) return dayjs(raw).format("DD MMM YYYY");
  return dayjs().format("DD MMM YYYY");
};

/**
 * Resolves the org's actual configured GST rate (0 if GST is disabled/unset —
 * see useOrganizationGstPercentage.js, same rule applied here).
 */
const resolveOrgGstPercentage = async (invoice) => {
  const organizationId =
    invoice?.organizationId ?? invoice?.organization_id ?? getOrganizationId();
  if (!organizationId) return 0;
  try {
    const activeSubscription = await getActiveSubscription(organizationId);
    if (
      activeSubscription?.includeGST !== false &&
      activeSubscription?.gstPercentage != null
    ) {
      return Number(activeSubscription.gstPercentage);
    }
    return 0;
  } catch (error) {
    console.warn("[invoiceReprintUtils] Could not load organization GST percentage:", error);
    return 0;
  }
};

const resolveGstPercentageLabel = async (invoice, discountedAmount) => {
  const base = Number(discountedAmount) || 0;
  const gstAmount =
    (Number(invoice?.sgst) || 0) + (Number(invoice?.cgst) || 0);
  if (base > 0 && gstAmount > 0) {
    const pct = (gstAmount / base) * 100;
    return Number.isInteger(pct) ? String(pct) : pct.toFixed(1);
  }
  // This invoice shows no GST actually charged — fall back to the org's real
  // configured rate (0 when GST is disabled) instead of a fixed guess, so the
  // label never claims a percentage that wasn't really applied.
  const orgPct = await resolveOrgGstPercentage(invoice);
  return String(orgPct);
};

const buildInvoiceMoneySummary = async (invoice) => {
  const baseAmount = Number(invoice?.amount) || 0;
  const discount = Number(invoice?.discount) || 0;
  const sgst = Number(invoice?.sgst) || 0;
  const cgst = Number(invoice?.cgst) || 0;
  const gstAmount = sgst + cgst;
  const discountedAmount = Math.max(0, baseAmount - discount);
  const totalAmount =
    Number(invoice?.totalAmount) || discountedAmount + gstAmount;
  const gstPct = await resolveGstPercentageLabel(invoice, discountedAmount);

  const summaryLines = [
    { label: "Subtotal (₹)", value: formatInvoiceInr(discountedAmount || baseAmount) },
  ];
  if (discount > 0) {
    summaryLines.push({
      label: "Total Discount (₹)",
      value: formatInvoiceInr(discount),
    });
  }
  summaryLines.push(
    {
      label: `GST @ ${gstPct}% (₹)`,
      value: formatInvoiceInr(gstAmount),
    },
    {
      label: "Total Payable Amount (₹)",
      value: formatInvoiceInr(totalAmount),
    }
  );

  return {
    baseAmount,
    discount,
    discountedAmount,
    sgst,
    cgst,
    gstAmount,
    totalAmount,
    gstPct,
    summaryLines,
  };
};

const buildLegacyGstSummaryLines = async (invoice) => {
  const money = await buildInvoiceMoneySummary(invoice);
  const halfGstLabel = (Number(money.gstPct) / 2).toFixed(1);
  const lines = [
    {
      label: "Base Amount",
      value: `Rs ${Number(money.baseAmount || 0).toFixed(2)}`,
    },
  ];
  if (money.discount > 0) {
    lines.push({
      label: "Discount",
      value: `Rs ${Number(money.discount).toFixed(2)}`,
    });
    lines.push({
      label: "Amount After Discount",
      value: `Rs ${Number(money.discountedAmount).toFixed(2)}`,
    });
  }
  lines.push(
    {
      label: `SGST (${halfGstLabel}%)`,
      value: `Rs ${Number(money.sgst).toFixed(2)}`,
    },
    {
      label: `CGST (${halfGstLabel}%)`,
      value: `Rs ${Number(money.cgst).toFixed(2)}`,
    },
    {
      label: "Total Amount",
      value: `Rs ${Number(money.totalAmount).toFixed(2)}`,
    }
  );
  return lines;
};

const resolveHospitalContext = async (user) => {
  const hospitalName = getOrganizationName(user) || "Aryan Hospital";
  let hospitalAddress = "";
  let hospitalPhone = "";
  const organizationId = getOrganizationId(user);
  if (organizationId) {
    try {
      const profile = await fetchOrganizationProfile(organizationId);
      hospitalAddress = profile?.address || "";
      hospitalPhone = profile?.phone || "";
    } catch (error) {
      console.warn(
        "[invoiceReprintUtils] Could not load organization profile:",
        error
      );
    }
  }
  return { hospitalName, hospitalAddress, hospitalPhone };
};

const resolvePatientRecord = async (invoice, patientsLookup = {}) => {
  const patientId = getInvoicePatientId(invoice);
  const lookup =
    patientsLookup && typeof patientsLookup === "object" ? patientsLookup : {};

  const fromLookupById = (id) => {
    if (id == null || id === "") return null;
    return (
      lookup[id] ||
      lookup[String(id)] ||
      (Number.isFinite(Number(id)) ? lookup[Number(id)] : null) ||
      null
    );
  };

  if (patientId != null && patientId !== "") {
    const fromLookup = fromLookupById(patientId);
    // Prefer a freshly loaded patient record so address/mobile/gender/age are complete.
    try {
      const fetched = await getPatientById(patientId);
      if (fetched) return { ...(fromLookup || {}), ...fetched };
    } catch (error) {
      console.warn(
        "[invoiceReprintUtils] Could not load patient for reprint:",
        error
      );
    }
    if (fromLookup) return fromLookup;
  }

  // Timeline / partial invoice payloads may omit patientId — use provided lookup.
  const lookupPatients = Object.values(lookup).filter(Boolean);
  if (lookupPatients.length > 0) {
    const preferred =
      lookupPatients.find(
        (p) => p?.id != null || p?.patientId != null || p?.name
      ) || lookupPatients[0];
    const fallbackId =
      preferred?.id ?? preferred?.patientId ?? Object.keys(lookup)[0] ?? null;
    if (fallbackId != null && fallbackId !== "") {
      try {
        const fetched = await getPatientById(fallbackId);
        if (fetched) return { ...preferred, ...fetched };
      } catch (error) {
        console.warn(
          "[invoiceReprintUtils] Could not load lookup patient for reprint:",
          error
        );
      }
    }
    return preferred;
  }

  return null;
};

const toPrintPatient = (invoice, patient) => {
  const patientId =
    getInvoicePatientId(invoice, patient) ||
    patient?.id ||
    invoice?.patientId ||
    "N/A";
  const name =
    patient?.name ||
    invoice?.patientName ||
    invoice?.toName ||
    invoice?.to_name ||
    "Patient";
  const age =
    patient?.age != null && patient.age !== ""
      ? patient.age
      : patient?.dob && dayjs(patient.dob).isValid()
        ? dayjs().diff(dayjs(patient.dob), "year")
        : "";

  return {
    id: patientId,
    name,
    gender: patient?.gender || "",
    age,
    dob: patient?.dob || "",
    mobile: patient?.mobile || patient?.phone || "",
    address: patient?.address || "",
  };
};

const resolveDoctorName = (invoice, medicineTx = []) => {
  if (hasText(invoice?.doctorName)) return String(invoice.doctorName).trim();
  if (hasText(invoice?.doctor_name)) return String(invoice.doctor_name).trim();
  const fromTx = medicineTx.find((tx) => hasText(tx?.doctorName || tx?.doctor_name));
  if (fromTx) {
    return String(fromTx.doctorName || fromTx.doctor_name).trim();
  }
  return "Doctor";
};

const getTransactionInvoiceId = (tx) =>
  tx?.invoiceId ?? tx?.invoice_id ?? tx?.invoice?.id ?? null;

const flattenMedicineTransactions = (transactions) => {
  const list = normalizeApiList(transactions);
  const rows = [];

  list.forEach((tx) => {
    const nested = normalizeApiList(tx?.medicines);
    if (nested.length > 0) {
      nested.forEach((med) => {
        rows.push({
          ...tx,
          medicineName:
            med.medicineName || med.name || med.medicine || tx.medicineName,
          name: med.medicineName || med.name || med.medicine || tx.medicineName,
          deductedQuantity:
            med.deductedQuantity ??
            med.quantity ??
            med.totalQuantity ??
            med.qty ??
            tx.deductedQuantity,
          totalQuantity:
            med.totalQuantity ??
            med.quantity ??
            med.deductedQuantity ??
            med.qty,
          totalCost: med.totalCost ?? med.amount ?? tx.totalCost,
          costPerUnit: med.costPerUnit ?? tx.costPerUnit,
          batchNumber: med.batchNumber ?? tx.batchNumber,
          expiryDate: med.expiryDate ?? tx.expiryDate,
        });
      });
      return;
    }
    rows.push(tx);
  });

  return rows;
};

const fetchMedicinesForInvoice = async (invoice) => {
  const invoiceId = invoice?.id ?? invoice?.invoiceId;
  const patientId = getInvoicePatientId(invoice);
  if (patientId == null || patientId === "") return [];

  try {
    const data = await getMedicineTransactionsByPatientId(patientId);
    const rows = flattenMedicineTransactions(data).filter((tx) => {
      const txInvoiceId = getTransactionInvoiceId(tx);
      if (invoiceId == null || txInvoiceId == null) return false;
      return String(txInvoiceId) === String(invoiceId);
    });
    return rows;
  } catch (error) {
    console.warn(
      "[invoiceReprintUtils] Could not load medicine transactions:",
      error
    );
    return [];
  }
};

const extractLabTestIds = (detail) => {
  if (!detail || typeof detail !== "object") return [];

  const candidates = [
    detail.labTestIds,
    detail.lab_test_ids,
    detail.labTestId,
    detail.lab_test_id,
    detail.testIds,
    detail.test_ids,
  ];

  const ids = [];
  const pushId = (value) => {
    if (value == null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach(pushId);
      return;
    }
    if (typeof value === "object") {
      const nestedId =
        value.id ?? value.labTestId ?? value.lab_test_id ?? value.testId;
      if (nestedId != null && nestedId !== "") {
        ids.push(nestedId);
      }
      return;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return;
      if (
        (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
        (trimmed.startsWith("{") && trimmed.endsWith("}"))
      ) {
        try {
          pushId(JSON.parse(trimmed));
          return;
        } catch {
          // fall through
        }
      }
      if (trimmed.includes(",")) {
        trimmed.split(",").forEach((part) => pushId(part.trim()));
        return;
      }
      ids.push(trimmed);
      return;
    }
    ids.push(value);
  };

  candidates.forEach(pushId);

  return Array.from(new Set(ids.map((id) => String(id)).filter(Boolean)));
};

const getReportTestName = (report) =>
  report?.testName ||
  report?.test_name ||
  report?.labTestName ||
  report?.name ||
  report?.resolvedTestName ||
  null;

const getReportLabTestId = (report) =>
  report?.labTestId ??
  report?.lab_test_id ??
  report?.testId ??
  report?.test_id ??
  null;

const isBilledLikeStatus = (status) => {
  const value = String(status || "")
    .trim()
    .toUpperCase();
  return (
    value === "BILLED" ||
    value === "COLLECTED" ||
    value === "COMPLETE" ||
    value === "COMPLETED"
  );
};

const getReportDateKey = (report) => {
  const reportDateRaw =
    report?.billedAt ||
    report?.billed_at ||
    report?.reportDate ||
    report?.createdAt ||
    report?.created_at ||
    report?.date ||
    report?.updatedAt ||
    report?.updated_at;
  if (!reportDateRaw || !dayjs(reportDateRaw).isValid()) return null;
  return dayjs(reportDateRaw).format("YYYY-MM-DD");
};

const toPositiveScopeId = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
};

const getInvoicePrescriptionId = (invoice) =>
  toPositiveScopeId(
    invoice?.prescriptionId ??
      invoice?.prescription_id ??
      invoice?.opdPrescriptionId ??
      invoice?.opd_prescription_id
  );

const getInvoiceAppointmentId = (invoice) =>
  toPositiveScopeId(invoice?.appointmentId ?? invoice?.appointment_id);

const getRecordPrescriptionId = (record) =>
  toPositiveScopeId(record?.prescriptionId ?? record?.prescription_id);

const getRecordAppointmentId = (record) =>
  toPositiveScopeId(record?.appointmentId ?? record?.appointment_id);

const scopeIdsMatch = (left, right) =>
  left != null && right != null && String(left) === String(right);

/**
 * Keep only lab reports/tests for one prescription or appointment visit.
 * Used so pathology invoice print does not merge an entire calendar day.
 */
export const filterReportsByVisitScope = (reports = [], scope = {}) => {
  const list = Array.isArray(reports) ? reports.filter(Boolean) : [];
  const prescriptionId =
    toPositiveScopeId(scope.prescriptionId ?? scope.prescription_id) ??
    getInvoicePrescriptionId(scope.invoice);
  const appointmentId =
    toPositiveScopeId(scope.appointmentId ?? scope.appointment_id) ??
    getInvoiceAppointmentId(scope.invoice);
  const extraPrescriptionIds = Array.isArray(scope.prescriptionIds)
    ? scope.prescriptionIds.map(toPositiveScopeId).filter(Boolean)
    : [];

  if (!prescriptionId && !appointmentId && !extraPrescriptionIds.length) {
    return list;
  }

  const prescriptionIdSet = new Set(extraPrescriptionIds.map(String));
  if (prescriptionId) prescriptionIdSet.add(String(prescriptionId));

  return list.filter((report) => {
    const reportPresc = getRecordPrescriptionId(report);
    const reportAppt = getRecordAppointmentId(report);
    if (prescriptionIdSet.size && reportPresc != null) {
      return prescriptionIdSet.has(String(reportPresc));
    }
    if (appointmentId && reportAppt != null) {
      return scopeIdsMatch(appointmentId, reportAppt);
    }
    return false;
  });
};

/** Split a day's reports into one group per prescription (or appointment). */
export const groupReportsByPrescriptionScope = (reports = []) => {
  const groups = new Map();
  (reports || []).forEach((report) => {
    if (!report) return;
    const prescId = getRecordPrescriptionId(report);
    const apptId = getRecordAppointmentId(report);
    const key =
      prescId != null
        ? `presc:${prescId}`
        : apptId != null
          ? `appt:${apptId}`
          : `report:${report.reportId ?? report.id ?? report.testName ?? groups.size}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(report);
  });
  return Array.from(groups.values());
};

const normalizeTestKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const uniqueTestsByIdOrName = (tests = []) => {
  const byId = new Map();
  const byName = new Map();

  tests.forEach((item) => {
    if (!item || !hasText(item.testName)) return;
    const testName = String(item.testName).trim();
    const nameKey = normalizeTestKey(testName);
    const id =
      item.labTestId != null && item.labTestId !== ""
        ? String(item.labTestId)
        : null;
    const next = {
      testName,
      amount: Number(item.amount) || 0,
      labTestId: id,
      dateKey: item.dateKey || null,
      billedLike: Boolean(item.billedLike),
    };

    if (id && byId.has(id)) {
      const existing = byId.get(id);
      byId.set(id, {
        ...existing,
        ...next,
        testName: existing.testName || next.testName,
        amount:
          Number(existing.amount) > 0 ? Number(existing.amount) : next.amount,
        billedLike: existing.billedLike || next.billedLike,
      });
      return;
    }

    if (byName.has(nameKey)) {
      const existing = byName.get(nameKey);
      const merged = {
        ...existing,
        ...next,
        testName: existing.testName || next.testName,
        labTestId: existing.labTestId || next.labTestId,
        amount:
          Number(existing.amount) > 0 ? Number(existing.amount) : next.amount,
        billedLike: existing.billedLike || next.billedLike,
      };
      byName.set(nameKey, merged);
      if (merged.labTestId) byId.set(String(merged.labTestId), merged);
      return;
    }

    if (id) byId.set(id, next);
    byName.set(nameKey, next);
  });

  // Prefer id-keyed entries; add name-only leftovers
  const result = [];
  const seenNames = new Set();
  byId.forEach((item) => {
    result.push(item);
    seenNames.add(normalizeTestKey(item.testName));
  });
  byName.forEach((item) => {
    const nameKey = normalizeTestKey(item.testName);
    if (seenNames.has(nameKey)) return;
    if (item.labTestId && byId.has(String(item.labTestId))) return;
    result.push(item);
    seenNames.add(nameKey);
  });
  return result;
};

const sumTestAmounts = (tests = []) =>
  (tests || []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

const amountsMatchInvoice = (tests, invoiceAmount) => {
  const target = Number(invoiceAmount) || 0;
  if (!(target > 0)) return true;
  return Math.abs(sumTestAmounts(tests) - target) <= 0.5;
};

/** Prefer a test set whose master prices match the invoice subtotal. */
const pickBestTestSet = (candidates = [], invoiceAmount = 0) => {
  const sets = (candidates || []).filter(
    (set) => Array.isArray(set) && set.length > 0
  );
  if (!sets.length) return [];

  const target = Number(invoiceAmount) || 0;
  if (!(target > 0)) {
    return uniqueTestsByIdOrName(sets.reduce((a, b) => (a.length >= b.length ? a : b), []));
  }

  let best = sets[0];
  let bestScore = Infinity;

  sets.forEach((set) => {
    const unique = uniqueTestsByIdOrName(set);
    const sum = sumTestAmounts(unique);
    const diff = Math.abs(sum - target);
    // Prefer exact/near amount match, then more tests (avoid dropping CBC), then closer sum.
    const score = diff * 1000 - unique.length;
    if (score < bestScore) {
      bestScore = score;
      best = unique;
    }
  });

  return uniqueTestsByIdOrName(best);
};

const mapReportsToTestItems = (reports = []) =>
  (reports || [])
    .map((report) => {
      const testName = getReportTestName(report);
      if (!hasText(testName)) return null;
      const status =
        report?.resultStatus ||
        report?.result_status ||
        report?.paymentStatus ||
        report?.status;
      return {
        testName: String(testName).trim(),
        amount:
          Number(
            report?.amount ??
              report?.price ??
              report?.rate ??
              report?.testPrice ??
              0
          ) || 0,
        labTestId: getReportLabTestId(report),
        dateKey: getReportDateKey(report),
        prescriptionId: getRecordPrescriptionId(report),
        appointmentId: getRecordAppointmentId(report),
        billedLike: isBilledLikeStatus(status) || status == null || status === "",
      };
    })
    .filter(Boolean);

const fetchTestsFromPrescriptions = async (invoice) => {
  const patientId = getInvoicePatientId(invoice);
  if (patientId == null || patientId === "") return [];

  try {
    const data = await getPrescriptionsByPatientId(patientId);
    const prescriptions = normalizeApiList(data);
    const invoicePrescriptionId = getInvoicePrescriptionId(invoice);
    const invoiceAppointmentId = getInvoiceAppointmentId(invoice);
    const visitScoped = Boolean(invoicePrescriptionId || invoiceAppointmentId);
    const invoiceDateKey = dayjs(
      invoice?.date ?? invoice?.createdAt ?? invoice?.created_at
    ).isValid()
      ? dayjs(invoice?.date ?? invoice?.createdAt ?? invoice?.created_at).format(
          "YYYY-MM-DD"
        )
      : null;

    const groups = [];
    const allTests = [];

    prescriptions.forEach((prescription) => {
      const prescId = toPositiveScopeId(
        prescription?.id ?? prescription?.prescriptionId
      );
      const prescApptId = toPositiveScopeId(
        prescription?.appointmentId ?? prescription?.appointment_id
      );

      if (invoicePrescriptionId && prescId !== invoicePrescriptionId) return;
      if (
        !invoicePrescriptionId &&
        invoiceAppointmentId &&
        prescApptId !== invoiceAppointmentId
      ) {
        return;
      }

      const labTests = normalizeApiList(
        prescription?.patientLabTests ||
          prescription?.labTests ||
          prescription?.tests
      );
      if (!labTests.length) return;

      const prescDateRaw =
        prescription?.createdAt ||
        prescription?.created_at ||
        prescription?.date ||
        prescription?.prescriptionDate;
      const prescDateKey =
        prescDateRaw && dayjs(prescDateRaw).isValid()
          ? dayjs(prescDateRaw).format("YYYY-MM-DD")
          : null;

      const mapped = labTests
        .map((test) => {
          const testName =
            test?.testName || test?.name || test?.labTestName || null;
          if (!hasText(testName)) return null;
          const status = test?.resultStatus || test?.result_status || test?.status;
          return {
            testName: String(testName).trim(),
            amount: Number(test?.amount ?? test?.price ?? test?.rate ?? 0) || 0,
            labTestId: test?.labTestId ?? test?.lab_test_id ?? test?.testId ?? null,
            dateKey: prescDateKey,
            billedLike:
              isBilledLikeStatus(status) || status == null || status === "",
          };
        })
        .filter(Boolean);

      if (!mapped.length) return;
      allTests.push(...mapped);

      const billedOnly = mapped.filter((item) => item.billedLike);
      if (billedOnly.length) {
        groups.push(billedOnly);
      }
      groups.push(mapped);

      if (
        !visitScoped &&
        invoiceDateKey &&
        (!prescDateKey || prescDateKey === invoiceDateKey)
      ) {
        groups.push(billedOnly.length ? billedOnly : mapped);
      }
    });

    // When invoice is tied to a prescription/appointment, never merge other visits.
    if (visitScoped) {
      return groups;
    }

    // Legacy: same-day union across prescriptions (only when invoice has no visit scope)
    if (invoiceDateKey) {
      const sameDay = allTests.filter(
        (item) => !item.dateKey || item.dateKey === invoiceDateKey
      );
      if (sameDay.length) groups.push(sameDay);
      const sameDayBilled = sameDay.filter((item) => item.billedLike);
      if (sameDayBilled.length) groups.push(sameDayBilled);
    }

    groups.push(allTests);
    return groups;
  } catch (error) {
    console.warn(
      "[invoiceReprintUtils] Could not load prescriptions for pathology reprint:",
      error
    );
    return [];
  }
};

const fetchPatientPathologyTestGroups = async (invoice) => {
  const patientId = getInvoicePatientId(invoice);
  const groups = [];
  if (patientId == null || patientId === "") return groups;

  let reports = [];
  try {
    reports = await getMergedPathLabReportsForPatient(patientId);
  } catch (mergedError) {
    console.warn(
      "[invoiceReprintUtils] getMergedPathLabReportsForPatient failed:",
      mergedError
    );
    try {
      reports = await getPathPabReportByPatient(patientId);
    } catch (patientError) {
      console.warn(
        "[invoiceReprintUtils] getPathPabReportByPatient failed:",
        patientError
      );
    }
  }

  try {
    const billed = await getBilledTests();
    const pid = Number(patientId);
    const billedForPatient = normalizeApiList(billed).filter(
      (row) => Number(row?.patientId ?? row?.patient_id) === pid
    );
    if (billedForPatient.length) {
      reports = [...normalizeApiList(reports), ...billedForPatient];
    }
  } catch (billedError) {
    console.warn(
      "[invoiceReprintUtils] getBilledTests failed for pathology reprint:",
      billedError
    );
  }

  const invoicePrescriptionId = getInvoicePrescriptionId(invoice);
  const invoiceAppointmentId = getInvoiceAppointmentId(invoice);
  const visitScoped = Boolean(invoicePrescriptionId || invoiceAppointmentId);

  let items = mapReportsToTestItems(normalizeApiList(reports));
  if (!items.length) return groups;

  if (visitScoped) {
    items = filterReportsByVisitScope(items, {
      prescriptionId: invoicePrescriptionId,
      appointmentId: invoiceAppointmentId,
    });
    if (items.length) groups.push(items);
    return groups;
  }

  const invoiceDateKey = dayjs(
    invoice?.date ?? invoice?.createdAt ?? invoice?.created_at
  ).isValid()
    ? dayjs(invoice?.date ?? invoice?.createdAt ?? invoice?.created_at).format(
        "YYYY-MM-DD"
      )
    : null;

  const billedLike = items.filter((item) => item.billedLike);
  const pool = billedLike.length ? billedLike : items;
  const sameDay = invoiceDateKey
    ? pool.filter((item) => !item.dateKey || item.dateKey === invoiceDateKey)
    : pool;

  if (sameDay.length) groups.push(sameDay);
  if (pool.length) groups.push(pool);
  groups.push(items);
  return groups;
};

const mapLabTestsByIds = async (labTestIds) => {
  if (!labTestIds.length) return [];

  const tests = await Promise.all(
    labTestIds.map(async (id) => {
      try {
        const test = await getLabTestById(id);
        return {
          labTestId: id,
          testName: test?.name || test?.testName || `Test #${id}`,
          amount: Number(test?.price ?? test?.amount ?? test?.rate ?? 0) || 0,
        };
      } catch (error) {
        console.warn(
          `[invoiceReprintUtils] Could not load lab test ${id}:`,
          error
        );
        return {
          labTestId: id,
          testName: `Test #${id}`,
          amount: 0,
        };
      }
    })
  );

  return tests.filter(Boolean);
};

let labTestMasterCache = null;
let labTestMasterCacheAt = 0;

const getLabTestMasterList = async () => {
  const now = Date.now();
  if (labTestMasterCache && now - labTestMasterCacheAt < 60 * 1000) {
    return labTestMasterCache;
  }
  try {
    const list = await getLabtestsList();
    labTestMasterCache = normalizeApiList(list);
    labTestMasterCacheAt = now;
    return labTestMasterCache;
  } catch (error) {
    console.warn("[invoiceReprintUtils] getLabtestsList failed:", error);
    return labTestMasterCache || [];
  }
};

const enrichTestsWithMasterPrices = async (tests) => {
  if (!tests.length) return [];

  const masterList = await getLabTestMasterList();
  const byId = new Map();
  const byName = new Map();
  masterList.forEach((test) => {
    if (!test) return;
    if (test.id != null) byId.set(String(test.id), test);
    const name = test.name || test.testName;
    if (hasText(name)) byName.set(normalizeTestKey(name), test);
  });

  const missingIds = Array.from(
    new Set(
      tests
        .map((item) => item.labTestId)
        .filter((id) => id != null && id !== "" && !byId.has(String(id)))
        .map(String)
    )
  );
  if (missingIds.length) {
    const fetched = await mapLabTestsByIds(missingIds);
    fetched.forEach((item) => {
      byId.set(String(item.labTestId), {
        id: item.labTestId,
        name: item.testName,
        amount: item.amount,
      });
    });
  }

  return tests.map((item) => {
    const byIdMatch =
      item.labTestId != null ? byId.get(String(item.labTestId)) : null;
    const byNameMatch = byName.get(normalizeTestKey(item.testName));
    const match = byIdMatch || byNameMatch;
    return {
      testName:
        item.testName ||
        match?.name ||
        match?.testName ||
        "Pathology Test",
      amount:
        Number(match?.amount ?? match?.price ?? match?.rate) > 0
          ? Number(match.amount ?? match.price ?? match.rate)
          : Number(item.amount) || 0,
      labTestId: item.labTestId ?? match?.id ?? null,
      dateKey: item.dateKey || null,
      billedLike: Boolean(item.billedLike),
    };
  });
};

/**
 * If known tests under-cover the invoice amount, find missing master tests
 * that complete the total (e.g. CBC ₹100 when 4 tests sum to ₹550 / invoice ₹650).
 */
const fillMissingTestsByInvoiceAmount = async (tests, invoiceAmount) => {
  const target = Number(invoiceAmount) || 0;
  const current = uniqueTestsByIdOrName(tests);
  if (!(target > 0) || amountsMatchInvoice(current, target)) {
    return current;
  }

  const shortfall = Number((target - sumTestAmounts(current)).toFixed(2));
  if (!(shortfall > 0.5)) return current;

  const masterList = await getLabTestMasterList();
  const presentNames = new Set(
    current.map((item) => normalizeTestKey(item.testName))
  );
  const presentIds = new Set(
    current
      .map((item) => (item.labTestId != null ? String(item.labTestId) : null))
      .filter(Boolean)
  );

  const candidates = masterList
    .map((test) => {
      const testName = test?.name || test?.testName;
      if (!hasText(testName)) return null;
      const amount = Number(test?.amount ?? test?.price ?? test?.rate ?? 0) || 0;
      if (!(amount > 0)) return null;
      const id = test?.id != null ? String(test.id) : null;
      if (id && presentIds.has(id)) return null;
      if (presentNames.has(normalizeTestKey(testName))) return null;
      return {
        testName: String(testName).trim(),
        amount,
        labTestId: id,
      };
    })
    .filter(Boolean);

  // Exact single-test fill (CBC case: shortfall 100)
  const exact = candidates.filter(
    (item) => Math.abs(item.amount - shortfall) <= 0.5
  );
  if (exact.length === 1) {
    return uniqueTestsByIdOrName([...current, exact[0]]);
  }
  if (exact.length > 1) {
    // Prefer common panel names if multiple master tests share the price
    const preferred = exact.find((item) =>
      ["cbc", "complete blood count", "complete blood count (cbc)"].includes(
        normalizeTestKey(item.testName)
      )
    );
    return uniqueTestsByIdOrName([...current, preferred || exact[0]]);
  }

  // Two-test combination fill for remaining shortfall
  for (let i = 0; i < candidates.length; i += 1) {
    for (let j = i + 1; j < candidates.length; j += 1) {
      if (Math.abs(candidates[i].amount + candidates[j].amount - shortfall) <= 0.5) {
        return uniqueTestsByIdOrName([
          ...current,
          candidates[i],
          candidates[j],
        ]);
      }
    }
  }

  return current;
};

const fetchTestsForInvoice = async (invoice) => {
  let detail = invoice;
  try {
    if (invoice?.id != null) {
      const fetched = await getInvoiceById(invoice.id);
      const unwrapped = unwrapInvoiceRecord(fetched);
      if (unwrapped && typeof unwrapped === "object") {
        detail = { ...invoice, ...unwrapped };
      } else if (fetched && typeof fetched === "object") {
        detail = { ...invoice, ...fetched };
      }
    }
  } catch (error) {
    console.warn(
      "[invoiceReprintUtils] Could not reload invoice for lab tests:",
      error
    );
  }

  const invoiceAmount = Number(detail?.amount) || 0;
  const labTestIds = extractLabTestIds(detail);
  const invoicePrescriptionId = getInvoicePrescriptionId(detail);
  const invoiceAppointmentId = getInvoiceAppointmentId(detail);
  const visitScoped = Boolean(invoicePrescriptionId || invoiceAppointmentId);
  const fromIds =
    labTestIds.length > 0 ? await mapLabTestsByIds(labTestIds) : [];

  const reportGroups = await fetchPatientPathologyTestGroups(detail);
  const prescriptionGroups = await fetchTestsFromPrescriptions(detail);

  const candidateSets = [];
  if (fromIds.length) candidateSets.push(fromIds);

  if (!visitScoped) {
    for (const group of [...reportGroups, ...prescriptionGroups]) {
      const enriched = await enrichTestsWithMasterPrices(group);
      if (enriched.length) candidateSets.push(enriched);
    }

    if (fromIds.length) {
      for (const group of [...reportGroups, ...prescriptionGroups]) {
        const enriched = await enrichTestsWithMasterPrices(group);
        candidateSets.push([...fromIds, ...enriched]);
      }
    }
  } else {
    for (const group of [...reportGroups, ...prescriptionGroups]) {
      const enriched = await enrichTestsWithMasterPrices(group);
      if (enriched.length) candidateSets.push(enriched);
    }
  }

  let selected = pickBestTestSet(candidateSets, invoiceAmount);
  if (visitScoped && labTestIds.length) {
    const idSet = new Set(labTestIds.map(String));
    const scoped = uniqueTestsByIdOrName(
      selected.filter(
        (item) => item.labTestId != null && idSet.has(String(item.labTestId))
      )
    );
    if (scoped.length) {
      selected = scoped;
    } else if (fromIds.length) {
      selected = fromIds;
    }
  }
  selected = await enrichTestsWithMasterPrices(selected);
  selected = await fillMissingTestsByInvoiceAmount(selected, invoiceAmount);

  if (selected.length > 0) {
    return selected.map(({ testName, amount }) => ({ testName, amount }));
  }

  return [
    {
      testName: "Pathology Test",
      amount: invoiceAmount,
    },
  ];
};

const fetchDischargeServices = async (invoice, user) => {
  const admissionId = invoice?.admissionId ?? invoice?.admission_id;
  if (admissionId == null || admissionId === "") return [];

  try {
    const organizationId = getOrganizationId(user);
    const data = await getIPDServices(admissionId, organizationId);
    return normalizeApiList(data).map((service) => ({
      type: service.type || service.serviceType || "Service",
      description: service.description || service.name || "N/A",
      quantity: service.quantity || 1,
      rate: Number(service.rate || service.unitPrice || 0),
      amount: Number(
        service.amount ||
          (Number(service.rate || service.unitPrice || 0) *
            Number(service.quantity || 1))
      ),
    }));
  } catch (error) {
    console.warn(
      "[invoiceReprintUtils] Could not load IPD services for discharge bill:",
      error
    );
    return [];
  }
};

const isIpdMedicineInvoice = (invoice, medicineRows = []) => {
  if (invoice?.admissionId || invoice?.admission_id) return true;
  if (String(invoice?.billType || "").toUpperCase() === "IPD") return true;
  return medicineRows.some((row) => {
    const patientType = String(row?.patientType || row?.patient_type || "")
      .trim()
      .toLowerCase();
    return patientType === "ipd" || patientType === "in";
  });
};

const printMedicineInvoice = async ({
  invoice,
  patient,
  hospital,
  medicineRows,
}) => {
  const money = await buildInvoiceMoneySummary(invoice);
  const doctorName = resolveDoctorName(invoice, medicineRows);
  const visitDate = resolveVisitDate(invoice);
  const notes = resolveInvoiceNotes(invoice);
  const printPatient = toPrintPatient(invoice, patient);

  if (isIpdMedicineInvoice(invoice, medicineRows)) {
    const tableRows =
      medicineRows.length > 0
        ? medicineRows.map((med) => [
            med.medicineName || med.name || med.medicine || "N/A",
            med.batchNumber || "N/A",
            med.expiryDate
              ? dayjs(med.expiryDate).isValid()
                ? dayjs(med.expiryDate).format("DD/MM/YYYY")
                : String(med.expiryDate)
              : "N/A",
            `${
              med.deductedQuantity ??
              med.totalQuantity ??
              med.quantity ??
              med.qty ??
              "—"
            } units`,
            Number(med.costPerUnit || 0).toFixed(2),
            Number(med.totalCost || 0).toFixed(2),
          ])
        : [
            [
              String(invoice?.particulars || "Medicine").trim() || "Medicine",
              "N/A",
              "N/A",
              "—",
              Number(money.baseAmount || 0).toFixed(2),
              Number(money.baseAmount || 0).toFixed(2),
            ],
          ];

    const bodyHtml = buildInvoiceDocumentHtml({
      hospitalName: hospital.hospitalName,
      hospitalAddress: hospital.hospitalAddress,
      hospitalPhone: hospital.hospitalPhone,
      title: "IPD MEDICINE INVOICE",
      metaLines: [
        { label: "Invoice ID", value: invoice?.id ?? "N/A" },
        { label: "Patient ID", value: printPatient.id },
        { label: "Patient", value: printPatient.name },
        { label: "Doctor", value: doctorName },
        { label: "Date", value: visitDate },
      ],
      tableHeaders: [
        "Medicine Name",
        "Batch Number",
        "Expiry Date",
        "Quantity",
        "Cost/Unit (Rs)",
        "Total Cost (Rs)",
      ],
      tableRows,
      summaryLines: await buildLegacyGstSummaryLines(invoice),
      notes,
    });

    await printPatientSummaryDocument({
      title: `IPD_Invoice_${printPatient.id}_${dayjs().format("YYYY-MM-DD")}`,
      bodyHtml,
    });
    return;
  }

  const medicines =
    medicineRows.length > 0
      ? medicineRows.map((med) => ({
          name: med.medicineName || med.name || med.medicine || "Medicine",
          quantity:
            med.deductedQuantity ??
            med.totalQuantity ??
            med.quantity ??
            med.qty ??
            null,
          totalCost: med.totalCost,
          amountLabel: formatInvoiceInr(med.totalCost),
        }))
      : [
          {
            name: String(invoice?.particulars || "Medicine").trim() || "Medicine",
            quantity: null,
            totalCost: money.baseAmount,
            amountLabel: formatInvoiceInr(money.baseAmount),
          },
        ];

  const bodyHtml = buildOpdMedicineInvoiceDocumentHtml({
    hospitalName: hospital.hospitalName,
    hospitalAddress: hospital.hospitalAddress,
    hospitalPhone: hospital.hospitalPhone,
    visitDate,
    doctorName,
    patient: printPatient,
    medicines,
    summaryLines: money.summaryLines,
    notes,
    invoiceNumber: invoice?.id ?? "",
    prescriptionId: getInvoicePrescriptionId(invoice) ?? "",
    appointmentId: getInvoiceAppointmentId(invoice) ?? "",
  });

  await printPatientSummaryDocument({
    title: "OPD Summary",
    bodyHtml,
  });
};

const printPathologyInvoice = async ({
  invoice,
  patient,
  hospital,
  tests,
}) => {
  const money = await buildInvoiceMoneySummary(invoice);
  const visitDate = resolveVisitDate(invoice);
  const doctorName = resolveDoctorName(invoice);
  const notes = resolveInvoiceNotes(invoice);
  const printPatient = toPrintPatient(invoice, patient);

  const printableTests =
    tests.length > 0
      ? tests
      : [{ testName: "Pathology Test", amount: money.baseAmount }];

  // If we only have aggregate amount on invoice, spread isn't needed —
  // show stored rates when present; otherwise put full amount on first row.
  const hasAnyAmount = printableTests.some((t) => Number(t.amount) > 0);
  const normalizedTests = printableTests.map((item, index) => {
    const amount = hasAnyAmount
      ? Number(item.amount) || 0
      : index === 0
        ? money.baseAmount
        : 0;
    return {
      testName: item.testName || item.name || "—",
      amount,
      rateLabel: formatInvoiceInr(amount),
    };
  });

  const bodyHtml = buildPathologyInvoiceDocumentHtml({
    hospitalName: hospital.hospitalName,
    hospitalAddress: hospital.hospitalAddress,
    hospitalPhone: hospital.hospitalPhone,
    visitDate,
    doctorName,
    patient: printPatient,
    tests: normalizedTests,
    summaryLines: money.summaryLines,
    notes,
    invoiceNumber: invoice?.id ?? "",
    prescriptionId: getInvoicePrescriptionId(invoice) ?? "",
    appointmentId: getInvoiceAppointmentId(invoice) ?? "",
  });

  await printPatientSummaryDocument({
    title: "Lab Test Invoice",
    bodyHtml,
  });
};

const printOpdServiceInvoice = async ({
  invoice,
  patient,
  hospital,
  serviceName,
}) => {
  const money = await buildInvoiceMoneySummary(invoice);
  const visitDate = resolveVisitDate(invoice);
  const doctorName = resolveDoctorName(invoice);
  const notes = resolveInvoiceNotes(invoice);
  const printPatient = toPrintPatient(invoice, patient);

  const bodyHtml = buildOpdServiceInvoiceDocumentHtml({
    hospitalName: hospital.hospitalName,
    hospitalAddress: hospital.hospitalAddress,
    hospitalPhone: hospital.hospitalPhone,
    visitDate,
    doctorName,
    patient: printPatient,
    serviceName,
    amountLabel: formatInvoiceInr(money.baseAmount || money.totalAmount),
    summaryLines: money.summaryLines,
    notes,
    invoiceNumber: invoice?.id ?? "",
    prescriptionId: getInvoicePrescriptionId(invoice) ?? "",
    appointmentId: getInvoiceAppointmentId(invoice) ?? "",
  });

  await printPatientSummaryDocument({
    title: `${serviceName} Invoice`,
    bodyHtml,
  });
};

const printDischargeInvoice = async ({
  invoice,
  patient,
  hospital,
  services,
}) => {
  const printPatient = toPrintPatient(invoice, patient);
  const visitDate = resolveVisitDate(invoice);
  const money = await buildInvoiceMoneySummary(invoice);
  const notes = resolveInvoiceNotes(invoice);
  const doctorName = resolveDoctorName(invoice);

  const tableRows =
    services.length > 0
      ? services.map((service) => [
          String((service.type || "N/A").toUpperCase()),
          service.description || "N/A",
          String(service.quantity || 1),
          Number(service.rate || 0).toFixed(2),
          Number(service.amount || 0).toFixed(2),
        ])
      : [
          [
            "DISCHARGE",
            String(invoice?.particulars || "IPD Discharge Bill").trim() ||
              "IPD Discharge Bill",
            "1",
            Number(money.totalAmount || 0).toFixed(2),
            Number(money.totalAmount || 0).toFixed(2),
          ],
        ];

  const bodyHtml = buildInvoiceDocumentHtml({
    hospitalName: hospital.hospitalName,
    hospitalAddress: hospital.hospitalAddress,
    hospitalPhone: hospital.hospitalPhone,
    title: "IPD DISCHARGE BILL",
    metaLines: [
      { label: "Bill ID", value: invoice?.id ?? "N/A" },
      { label: "Date", value: visitDate },
      { label: "Patient", value: printPatient.name },
      { label: "Patient ID", value: printPatient.id },
      {
        label: "Admission ID",
        value: invoice?.admissionId ?? invoice?.admission_id ?? "N/A",
      },
      { label: "Treating Doctor", value: doctorName },
      {
        label: "Payment Status",
        value: invoice?.paymentStatus || "N/A",
      },
      {
        label: "Payment Method",
        value: invoice?.paymentMode || "N/A",
      },
    ],
    tableHeaders: [
      "Service Type",
      "Description",
      "Quantity",
      "Rate (Rs)",
      "Amount (Rs)",
    ],
    tableRows,
    summaryLines: [
      {
        label: "Total Amount",
        value: `Rs ${Number(money.totalAmount || 0).toFixed(2)}`,
      },
    ],
    notes,
  });

  await printPatientSummaryDocument({
    title: `IPD Discharge Bill - ${invoice?.id ?? "invoice"}`,
    bodyHtml,
  });
};

const resolvePartyFromName = (invoice) => {
  const fromName =
    invoice?.fromName ||
    invoice?.from_name ||
    invoice?.supplierName ||
    invoice?.supplier_name ||
    "";
  return hasText(fromName) ? String(fromName).trim() : "";
};

const resolveInvoiceTypeLabel = (invoice) => {
  const type = String(
    resolveInvoiceDisplayType(invoice) ||
      invoice?.invoiceType ||
      invoice?.invoice_type ||
      ""
  )
    .trim()
    .toUpperCase();
  return type || "";
};

const resolveSupplierDetailsForPrint = async (invoice) => {
  const fallbackName = resolvePartyFromName(invoice) || "Supplier";
  let name = fallbackName;
  let address =
    invoice?.supplierAddress ||
    invoice?.supplier_address ||
    invoice?.address ||
    "";
  let mobile =
    invoice?.supplierContact ||
    invoice?.supplier_contact ||
    invoice?.contact ||
    invoice?.mobile ||
    invoice?.phone ||
    "";
  let gstNumber =
    invoice?.supplierGstNumber ||
    invoice?.supplier_gst_number ||
    invoice?.gstNumber ||
    invoice?.gst_number ||
    "";

  const supplierId = invoice?.supplierId ?? invoice?.supplier_id ?? null;

  const applySupplier = (supplier) => {
    if (!supplier || typeof supplier !== "object") return;
    name =
      supplier.name ||
      supplier.supplierName ||
      supplier.supplier_name ||
      name;
    address =
      supplier.address ||
      supplier.supplierAddress ||
      supplier.supplier_address ||
      address;
    mobile =
      supplier.contact ||
      supplier.supplierContact ||
      supplier.phone ||
      supplier.mobile ||
      mobile;
    gstNumber =
      supplier.gstNumber ||
      supplier.supplierGstNumber ||
      supplier.gst_number ||
      gstNumber;
  };

  try {
    if (supplierId != null && supplierId !== "") {
      const byId = await getSupplierById(supplierId);
      applySupplier(byId);
    }
  } catch (error) {
    console.warn(
      "[invoiceReprintUtils] Could not load supplier by id for expense print:",
      error
    );
  }

  if (!hasText(address) || !hasText(mobile) || !hasText(gstNumber)) {
    try {
      const suppliers = await getAllSuppliers();
      const nameKey = String(fallbackName).trim().toLowerCase();
      const match = normalizeApiList(suppliers).find((supplier) => {
        const supplierName = String(
          supplier?.name || supplier?.supplierName || ""
        )
          .trim()
          .toLowerCase();
        if (supplierId != null && String(supplier?.id) === String(supplierId)) {
          return true;
        }
        return supplierName && supplierName === nameKey;
      });
      if (match) applySupplier(match);
    } catch (error) {
      console.warn(
        "[invoiceReprintUtils] Could not load suppliers for expense print:",
        error
      );
    }
  }

  return {
    name: hasText(name) ? String(name).trim() : fallbackName,
    address: hasText(address) ? String(address).trim() : "",
    mobile: hasText(mobile) ? String(mobile).trim() : "",
    gstNumber: hasText(gstNumber) ? String(gstNumber).trim() : "",
  };
};

const formatMoneyCell = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fetchExpensePurchaseLineItems = async (invoice) => {
  const invoiceId = invoice?.id ?? invoice?.invoiceId;
  if (invoiceId == null || invoiceId === "") return [];

  const [medicineInventory, pathInventory] = await Promise.all([
    getMedicineInventoryList().catch((error) => {
      console.warn(
        "[invoiceReprintUtils] Could not load medicine inventory for expense print:",
        error
      );
      return [];
    }),
    getAllInventory().catch((error) => {
      console.warn(
        "[invoiceReprintUtils] Could not load pathology inventory for expense print:",
        error
      );
      return [];
    }),
  ]);

  const linkedRows = [
    ...normalizeApiList(medicineInventory).map((row) => ({
      ...row,
      sourceType: "medicine",
    })),
    ...normalizeApiList(pathInventory).map((row) => ({
      ...row,
      sourceType: "pathology",
    })),
  ].filter(
    (row) =>
      String(row?.invoiceId ?? row?.invoice_id ?? "") === String(invoiceId)
  );

  return linkedRows.map((row) => {
    const name = String(
      row?.name || row?.itemName || row?.medicineName || row?.testName || "Item"
    ).trim();
    const batchNumber = String(
      row?.batchNumber || row?.batch_number || ""
    ).trim();
    const quantity = Number(row?.quantity ?? row?.qty ?? 0) || 0;
    const expiryRaw =
      row?.expirationDate ||
      row?.expiryDate ||
      row?.expiry ||
      row?.expiration_date ||
      null;
    const expiryDate =
      expiryRaw && dayjs(expiryRaw).isValid()
        ? dayjs(expiryRaw).format("DD MMM YYYY")
        : expiryRaw
          ? String(expiryRaw)
          : "—";

    const systemLineAmount = Number(row?.price ?? row?.systemPrice ?? 0) || 0;
    const supplierLineAmount =
      Number(row?.vendorPrice ?? row?.vendor_price ?? 0) || 0;

    const systemPriceLabel = formatMoneyCell(systemLineAmount);
    const supplierPriceLabel = formatMoneyCell(supplierLineAmount);

    return {
      name: name || "Item",
      batchNumber: batchNumber || "—",
      quantity: quantity > 0 ? String(quantity) : "—",
      expiryDate,
      systemPriceLabel,
      supplierPriceLabel,
      amount:
        supplierLineAmount > 0 ? supplierLineAmount : systemLineAmount,
    };
  });
};

/**
 * Donation / Misc / Expense reprint — same visual layout as
 * registration/consultation (no inner title like "DONATION INVOICE").
 * Uses party details header (not patient ID/Name block).
 */
const printServiceStyleInvoice = async ({
  invoice,
  hospital,
  serviceName,
  lineItems = null,
  itemColumnLabel = "Service",
  partyDetails,
  tableHeaders = null,
  tableRows = null,
  compactTable = false,
}) => {
  const money = await buildInvoiceMoneySummary(invoice);
  const visitDate = resolveVisitDate(invoice);
  const notes = resolveInvoiceNotes(invoice);
  const invoiceType = resolveInvoiceTypeLabel(invoice);

  const resolvedLineItems =
    Array.isArray(lineItems) && lineItems.length > 0
      ? lineItems
      : [
          {
            name: serviceName || invoiceType || "Invoice",
            amountLabel: formatInvoiceInr(
              money.baseAmount || money.totalAmount
            ),
          },
        ];

  const bodyHtml = buildOpdServiceInvoiceDocumentHtml({
    hospitalName: hospital.hospitalName,
    hospitalAddress: hospital.hospitalAddress,
    hospitalPhone: hospital.hospitalPhone,
    visitDate,
    doctorName: "",
    patient: null,
    partyDetails: {
      mode: partyDetails?.mode || "fromName",
      name: partyDetails?.name || resolvePartyFromName(invoice) || "N/A",
      address: partyDetails?.address || "",
      mobile: partyDetails?.mobile || "",
      invoiceNumber: partyDetails?.invoiceNumber || "",
      gstNumber: partyDetails?.gstNumber || "",
      invoiceType:
        partyDetails?.showInvoiceType === false
          ? ""
          : partyDetails?.invoiceType || invoiceType,
      showInvoiceType: partyDetails?.showInvoiceType !== false,
      visitDate,
    },
    serviceName: serviceName || invoiceType || "Invoice",
    amountLabel: formatInvoiceInr(money.baseAmount || money.totalAmount),
    lineItems: resolvedLineItems,
    itemColumnLabel,
    tableHeaders,
    tableRows,
    compactTable,
    summaryLines: money.summaryLines,
    notes,
    invoiceNumber: invoice?.id ?? "",
  });

  await printPatientSummaryDocument({
    title: "Invoice",
    bodyHtml,
  });
};

const printDonationOrMiscInvoice = async ({ invoice, hospital }) => {
  const fromName = resolvePartyFromName(invoice) || "N/A";
  const invoiceType = resolveInvoiceTypeLabel(invoice) || "DONATION";
  await printServiceStyleInvoice({
    invoice,
    hospital,
    // Header: From Name only (no Invoice Type — already in Service column)
    serviceName: invoiceType,
    partyDetails: {
      mode: "fromName",
      name: fromName,
      showInvoiceType: false,
    },
    itemColumnLabel: "Service",
  });
};

const printExpenseInvoice = async ({ invoice, hospital }) => {
  const invoiceType = resolveInvoiceTypeLabel(invoice);
  const supplier = await resolveSupplierDetailsForPrint(invoice);
  const purchaseItems = await fetchExpensePurchaseLineItems(invoice);
  const money = await buildInvoiceMoneySummary(invoice);
  const invoiceNumber =
    invoice?.invoiceNumber ||
    invoice?.invoice_number ||
    "";
  const visitDate = resolveVisitDate(invoice);

  const tableHeaders = [
    "#",
    "Item Name",
    "Batch No.",
    "Qty",
    "Expiry",
    "System Price",
    "Supplier Price",
  ];

  const tableRows =
    purchaseItems.length > 0
      ? purchaseItems.map((item, index) => [
          String(index + 1),
          item.name || "—",
          item.batchNumber || "—",
          item.quantity || "—",
          item.expiryDate || "—",
          item.systemPriceLabel || "—",
          item.supplierPriceLabel || "—",
        ])
      : [
          [
            "1",
            supplier.name || "Purchase",
            "—",
            "—",
            "—",
            formatMoneyCell(money.baseAmount || money.totalAmount),
            "—",
          ],
        ];

  await printServiceStyleInvoice({
    invoice,
    hospital,
    partyDetails: {
      mode: "supplier",
      name: supplier.name,
      address: supplier.address,
      mobile: supplier.mobile,
      gstNumber: supplier.gstNumber,
      invoiceNumber,
      invoiceType,
      showInvoiceType: true,
      visitDate,
    },
    tableHeaders,
    tableRows,
    compactTable: true,
  });
};

/**
 * Reprint an invoice from InvoiceTable using the same document builders
 * as the original create/print flows for each invoice type.
 */
export const printInvoiceFromTable = async (
  invoice,
  {
    user = null,
    patientsLookup = {},
    preferredPathologyTests = null,
  } = {}
) => {
  if (!invoice) {
    throw new Error("Invoice not found");
  }

  const hospital = await resolveHospitalContext(user);
  const patient = await resolvePatientRecord(invoice, patientsLookup);
  const displayType = String(resolveInvoiceDisplayType(invoice) || "")
    .trim()
    .toUpperCase();
  const rawType = String(invoice?.invoiceType || invoice?.invoice_type || "")
    .trim()
    .toUpperCase();

  if (isExpenseInvoice(invoice)) {
    await printExpenseInvoice({
      invoice,
      hospital,
    });
    return;
  }

  if (displayType === "DISCHARGE" || invoice?.admissionId || invoice?.admission_id) {
    // Medicine invoices linked to admission use medicine print format.
    if (rawType === "MEDICINE") {
      const medicineRows = await fetchMedicinesForInvoice(invoice);
      await printMedicineInvoice({
        invoice,
        patient,
        hospital,
        medicineRows,
      });
      return;
    }

    const services = await fetchDischargeServices(invoice, user);
    await printDischargeInvoice({
      invoice,
      patient,
      hospital,
      services,
    });
    return;
  }

  if (rawType === "MEDICINE" || displayType === "MEDICINE") {
    const medicineRows = await fetchMedicinesForInvoice(invoice);
    await printMedicineInvoice({
      invoice,
      patient,
      hospital,
      medicineRows,
    });
    return;
  }

  if (rawType === "PATHOLOGYTEST" || displayType === "PATHOLOGYTEST") {
    let tests = [];
    const hasPreferred =
      Array.isArray(preferredPathologyTests) && preferredPathologyTests.length > 0;

    const mapPreferredRows = () =>
      preferredPathologyTests
        .map((item) => {
          const testName = String(
            item?.testName || item?.name || item?.resolvedTestName || ""
          ).trim();
          if (!testName) return null;
          return {
            testName,
            amount: Number(item?.amount ?? item?.price ?? item?.rate ?? 0) || 0,
            labTestId:
              item?.labTestId ?? item?.lab_test_id ?? item?.testId ?? item?.test_id ?? null,
          };
        })
        .filter(Boolean);

    // Use preferred test names from the row/day group, then fill rates from master catalog
    // (one cached list call) so we do not print ₹0.00 for every line.
    if (hasPreferred) {
      tests = await enrichTestsWithMasterPrices(mapPreferredRows());
      const missingPriceCount = tests.filter((item) => !(Number(item?.amount) > 0)).length;
      if (missingPriceCount > 0) {
        const preferredIds = mapPreferredRows()
          .map((item) => item.labTestId)
          .filter((id) => id != null && id !== "");
        const labTestIds =
          preferredIds.length > 0 ? preferredIds : extractLabTestIds(invoice);
        if (labTestIds.length > 0) {
          const fromIds = await mapLabTestsByIds(labTestIds);
          if (fromIds.length) {
            const byName = new Map(
              fromIds.map((item) => [
                String(item.testName || "")
                  .trim()
                  .toLowerCase(),
                item,
              ])
            );
            const byId = new Map(
              fromIds
                .filter((item) => item.labTestId != null)
                .map((item) => [String(item.labTestId), item])
            );
            tests = tests.map((item) => {
              if (Number(item.amount) > 0) return item;
              const match =
                (item.labTestId != null ? byId.get(String(item.labTestId)) : null) ||
                byName.get(String(item.testName || "").trim().toLowerCase());
              if (match && Number(match.amount) > 0) {
                return { ...item, amount: Number(match.amount) || 0 };
              }
              return item;
            });
          }
        }
      }
    }

    const pricedCount = tests.filter((item) => Number(item?.amount) > 0).length;
    const stillNeedsInvoiceFallback =
      !hasPreferred &&
      (!tests.length || pricedCount === 0 || pricedCount < Math.ceil(tests.length / 2));

    if (stillNeedsInvoiceFallback) {
      const invoiceTests = await fetchTestsForInvoice(invoice);
      const invoicePriced = invoiceTests.filter((item) => Number(item?.amount) > 0).length;
      if (invoicePriced > pricedCount) {
        tests = invoiceTests;
      }
    } else if (hasPreferred && tests.length > 0 && pricedCount === 0) {
      // Enrich prices only — never expand to other prescriptions / whole-day tests.
      const labTestIds = extractLabTestIds(invoice);
      if (labTestIds.length > 0) {
        const fromIds = await mapLabTestsByIds(labTestIds);
        if (fromIds.length) {
          const byId = new Map(
            fromIds
              .filter((item) => item.labTestId != null)
              .map((item) => [String(item.labTestId), item])
          );
          const byName = new Map(
            fromIds.map((item) => [
              String(item.testName || "")
                .trim()
                .toLowerCase(),
              item,
            ])
          );
          tests = tests.map((item) => {
            if (Number(item.amount) > 0) return item;
            const match =
              (item.labTestId != null ? byId.get(String(item.labTestId)) : null) ||
              byName.get(String(item.testName || "").trim().toLowerCase());
            if (match && Number(match.amount) > 0) {
              return { ...item, amount: Number(match.amount) || 0 };
            }
            return item;
          });
        }
      }
    }

    const isGenericFallback =
      !tests.length ||
      (tests.length === 1 &&
        String(tests[0]?.testName || "")
          .trim()
          .toLowerCase() === "pathology test");

    if (isGenericFallback && hasPreferred) {
      tests = await enrichTestsWithMasterPrices(mapPreferredRows());
    }

    await printPathologyInvoice({
      invoice,
      patient,
      hospital,
      tests: tests.map(({ testName, amount }) => ({
        testName,
        amount: Number(amount) || 0,
      })),
    });
    return;
  }

  if (rawType === "REGISTRATION" || displayType === "REGISTRATION") {
    await printOpdServiceInvoice({
      invoice,
      patient,
      hospital,
      serviceName: "Registration",
    });
    return;
  }

  if (rawType === "CONSULTATION" || displayType === "CONSULTATION") {
    await printOpdServiceInvoice({
      invoice,
      patient,
      hospital,
      serviceName: "Consultation",
    });
    return;
  }

  if (rawType === "DONATION" || displayType === "DONATION") {
    await printDonationOrMiscInvoice({
      invoice,
      hospital,
    });
    return;
  }

  if (rawType === "MISC" || displayType === "MISC") {
    await printDonationOrMiscInvoice({
      invoice,
      hospital,
    });
    return;
  }

  // Fallback — same service-style layout (no inner title heading)
  await printServiceStyleInvoice({
    invoice,
    hospital,
    serviceName: resolveInvoiceTypeLabel(invoice) || "Invoice",
    partyDetails: {
      mode: "fromName",
      name:
        resolvePartyFromName(invoice) ||
        invoice?.patientName ||
        invoice?.toName ||
        "N/A",
      invoiceType: resolveInvoiceTypeLabel(invoice),
    },
  });
};

const isPathologyInvoice = (invoice) => {
  const rawType = String(invoice?.invoiceType || invoice?.invoice_type || "")
    .trim()
    .toUpperCase();
  const displayType = String(resolveInvoiceDisplayType(invoice) || "")
    .trim()
    .toUpperCase();
  const particulars = String(invoice?.particulars || "").trim().toUpperCase();
  return (
    rawType === "PATHOLOGYTEST" ||
    displayType === "PATHOLOGYTEST" ||
    rawType.includes("PATHOLOGY") ||
    displayType.includes("PATHOLOGY") ||
    particulars.includes("PATHOLOGY")
  );
};

const getInvoiceSortTimestamp = (invoice) => {
  const raw =
    invoice?.date ||
    invoice?.createdAt ||
    invoice?.created_at ||
    invoice?.invoiceDate ||
    null;
  if (!raw) return 0;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.valueOf() : 0;
};

/**
 * Find the best-matching pathology invoice for a lab-test/report row.
 * Prefers invoices that include the same labTestId, then falls back to the
 * latest pathology invoice for the patient.
 */
export const findPathologyInvoiceForLabRecord = async (
  patientId,
  match = {}
) => {
  if (patientId == null || patientId === "") return null;

  let invoices = [];
  try {
    invoices = normalizeApiList(await getInvoicesByPatientId(patientId));
  } catch (error) {
    console.warn(
      "[invoiceReprintUtils] Could not load patient invoices for pathology reprint:",
      error
    );
    return null;
  }

  const pathologyInvoices = invoices
    .filter(isPathologyInvoice)
    .sort((a, b) => getInvoiceSortTimestamp(b) - getInvoiceSortTimestamp(a));

  if (!pathologyInvoices.length) return null;

  const matchPrescriptionId = toPositiveScopeId(
    match.prescriptionId ?? match.prescription_id
  );
  const matchAppointmentId = toPositiveScopeId(
    match.appointmentId ?? match.appointment_id
  );
  const labTestId =
    match.labTestId ?? match.lab_test_id ?? match.testId ?? match.test_id ?? null;
  const reportId = match.reportId ?? match.report_id ?? match.id ?? null;

  const scoreInvoice = (invoice) => {
    let score = 0;
    const invPresc = getInvoicePrescriptionId(invoice);
    const invAppt = getInvoiceAppointmentId(invoice);

    if (matchPrescriptionId && invPresc === matchPrescriptionId) score += 100;
    if (matchAppointmentId && invAppt === matchAppointmentId) score += 90;

    if (labTestId != null && labTestId !== "") {
      if (
        extractLabTestIds(invoice).some((id) => String(id) === String(labTestId))
      ) {
        score += 50;
      }
    }

    if (reportId != null && reportId !== "") {
      const candidates = [
        invoice.reportId,
        invoice.report_id,
        invoice.pathLabReportId,
        ...(Array.isArray(invoice.reportIds) ? invoice.reportIds : []),
      ];
      if (candidates.some((id) => id != null && String(id) === String(reportId))) {
        score += 40;
      }
    }

    return score;
  };

  const scored = pathologyInvoices
    .map((invoice) => ({
      invoice,
      score: scoreInvoice(invoice),
      timestamp: getInvoiceSortTimestamp(invoice),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return b.timestamp - a.timestamp;
    });

  if (scored[0]?.score > 0) {
    return scored[0].invoice;
  }

  if (matchPrescriptionId || matchAppointmentId) {
    return null;
  }

  if (labTestId != null && labTestId !== "") {
    const byLabTest = pathologyInvoices.find((invoice) =>
      extractLabTestIds(invoice).some((id) => String(id) === String(labTestId))
    );
    if (byLabTest) return byLabTest;
  }

  if (reportId != null && reportId !== "") {
    const byReport = pathologyInvoices.find((invoice) => {
      const candidates = [
        invoice.reportId,
        invoice.report_id,
        invoice.pathLabReportId,
        ...(Array.isArray(invoice.reportIds) ? invoice.reportIds : []),
      ];
      return candidates.some((id) => id != null && String(id) === String(reportId));
    });
    if (byReport) return byReport;
  }

  return pathologyInvoices[0];
};

/**
 * Print the pathology invoice linked to a lab-test/report row
 * (same reprint path used by InvoiceTable).
 */
export const printPathologyLabInvoiceFromRecord = async (
  record,
  { user = null, patientsLookup = {} } = {}
) => {
  if (!record) {
    throw new Error("Lab test record not found");
  }

  const patientId =
    record.patientId ??
    record.patient_id ??
    record.patient?.id ??
    null;

  if (patientId == null || patientId === "") {
    throw new Error("Patient ID is required to print pathology invoice");
  }

  const invoice = await findPathologyInvoiceForLabRecord(patientId, {
    prescriptionId:
      record.prescriptionId ??
      record.prescription_id ??
      null,
    appointmentId:
      record.appointmentId ??
      record.appointment_id ??
      null,
    labTestId:
      record.labTestId ??
      record.lab_test_id ??
      record.testId ??
      record.test_id ??
      null,
    reportId: record.reportId ?? record.report_id ?? record.id ?? null,
  });

  if (!invoice) {
    throw new Error("No pathology invoice found for this lab test");
  }

  const doctorName =
    record.doctorName ||
    record.resolvedDoctorName ||
    record.assignedByName ||
    invoice.doctorName ||
    invoice.doctor_name ||
    null;

  const patientName =
    record.patientName ||
    record.patient_name ||
    invoice.patientName ||
    invoice.toName ||
    invoice.to_name ||
    null;

  // Patient invoices from list APIs can omit patientId / labTestIds; enrich from row.
  const enrichedInvoice = {
    ...invoice,
    patientId: getInvoicePatientId(invoice) || patientId,
    patient_id: invoice.patient_id || patientId,
    patientName: invoice.patientName || patientName,
    toName: invoice.toName || invoice.to_name || patientName,
    doctorName: invoice.doctorName || invoice.doctor_name || doctorName,
    doctor_name: invoice.doctor_name || invoice.doctorName || doctorName,
  };

  const labTestId =
    record.labTestId ??
    record.lab_test_id ??
    record.testId ??
    record.test_id ??
    null;
  if (
    labTestId != null &&
    labTestId !== "" &&
    extractLabTestIds(enrichedInvoice).length === 0
  ) {
    enrichedInvoice.labTestIds = [labTestId];
  }

  const lookup = { ...(patientsLookup || {}) };
  if (!lookup[patientId] && !lookup[String(patientId)]) {
    try {
      const patient = await getPatientById(patientId);
      if (patient) {
        lookup[patientId] = patient;
        lookup[String(patientId)] = patient;
        const numericId = Number(patientId);
        if (!Number.isNaN(numericId)) {
          lookup[numericId] = patient;
        }
      }
    } catch (error) {
      console.warn(
        "[invoiceReprintUtils] Could not load patient for pathology invoice reprint:",
        error
      );
    }
  }

  if (!lookup[patientId] && !lookup[String(patientId)]) {
    lookup[patientId] = {
      id: patientId,
      name: patientName || "Patient",
      gender: record.gender || record.patientGender || "",
      dob: record.dob || record.patientDob || "",
      age: record.age || record.patientAge || "",
      mobile: record.mobile || record.phone || record.patientMobile || "",
      address: record.address || record.patientAddress || "",
    };
    lookup[String(patientId)] = lookup[patientId];
  }

  const preferredTestName = String(
    record.testName ||
      record.resolvedTestName ||
      record.labTestName ||
      record.test_name ||
      ""
  ).trim();

  const recordPrescriptionId =
    record.prescriptionId ?? record.prescription_id ?? null;

  const prescriptionTests = Array.isArray(record.patientLabTests)
    ? record.patientLabTests
    : Array.isArray(record.labTests)
      ? record.labTests
      : [];

  const mapPrescriptionTests = () =>
    prescriptionTests
      .map((test) => {
        const name = String(
          test?.testName ||
            test?.resolvedTestName ||
            test?.name ||
            test?.labTestName ||
            ""
        ).trim();
        if (!name) return null;
        return {
          testName: name,
          amount: Number(test?.amount ?? test?.price ?? test?.rate ?? 0) || 0,
          labTestId:
            test?.labTestId ?? test?.lab_test_id ?? test?.testId ?? test?.test_id ?? null,
        };
      })
      .filter(Boolean);

  const invoiceLabTestIds = extractLabTestIds(enrichedInvoice);
  let mappedPrescriptionTests = mapPrescriptionTests();

  if (mappedPrescriptionTests.length > 0) {
    const siblingIds = mappedPrescriptionTests
      .map((test) => test.labTestId)
      .filter((id) => id != null && id !== "");
    if (siblingIds.length) {
      enrichedInvoice.labTestIds = Array.from(new Set(siblingIds.map(String)));
    }
    if (recordPrescriptionId) {
      enrichedInvoice.prescriptionId =
        enrichedInvoice.prescriptionId ??
        enrichedInvoice.prescription_id ??
        recordPrescriptionId;
    }
  } else if (recordPrescriptionId && labTestId != null && labTestId !== "") {
    mappedPrescriptionTests = [
      {
        testName: preferredTestName || `Test #${labTestId}`,
        amount:
          Number(
            record.amount ??
              record.price ??
              record.rate ??
              record.testAmount ??
              0
          ) || 0,
        labTestId,
      },
    ];
    enrichedInvoice.labTestIds = [String(labTestId)];
    enrichedInvoice.prescriptionId =
      enrichedInvoice.prescriptionId ??
      enrichedInvoice.prescription_id ??
      recordPrescriptionId;
  }

  let preferredPathologyTests = null;
  if (mappedPrescriptionTests.length > 0) {
    preferredPathologyTests = mappedPrescriptionTests;
  } else if (
    labTestId != null &&
    labTestId !== "" &&
    preferredTestName &&
    invoiceLabTestIds.length > 1
  ) {
    // Combined invoice in DB — print only the clicked test line.
    preferredPathologyTests = [
      {
        testName: preferredTestName,
        amount:
          Number(
            record.amount ??
              record.price ??
              record.rate ??
              record.testAmount ??
              0
          ) || 0,
        labTestId,
      },
    ];
    enrichedInvoice.labTestIds = [String(labTestId)];
  } else if (preferredTestName) {
    preferredPathologyTests = [
      {
        testName: preferredTestName,
        amount:
          Number(
            record.amount ??
              record.price ??
              record.rate ??
              record.testAmount ??
              0
          ) || 0,
        labTestId,
      },
    ];
    if (labTestId != null && labTestId !== "") {
      enrichedInvoice.labTestIds = [String(labTestId)];
    }
  }

  await printInvoiceFromTable(enrichedInvoice, {
    user,
    patientsLookup: lookup,
    preferredPathologyTests,
  });
  return invoice;
};

const getInvoiceDateKey = (invoice) => {
  const raw =
    invoice?.date ||
    invoice?.createdAt ||
    invoice?.created_at ||
    invoice?.invoiceDate ||
    null;
  if (!raw || !dayjs(raw).isValid()) return null;
  return dayjs(raw).format("YYYY-MM-DD");
};

/**
 * Find the best pathology invoice for a day group of lab reports.
 */
export const findPathologyInvoiceForDayReports = async (
  patientId,
  reports = [],
  dateKey = null,
  scope = {}
) => {
  if (patientId == null || patientId === "") return null;

  const scopedReports = filterReportsByVisitScope(reports, scope);
  const reportList = scopedReports.length ? scopedReports : reports;

  let invoices = [];
  try {
    invoices = normalizeApiList(await getInvoicesByPatientId(patientId));
  } catch (error) {
    console.warn(
      "[invoiceReprintUtils] Could not load patient invoices for day pathology reprint:",
      error
    );
    return null;
  }

  const pathologyInvoices = invoices.filter(isPathologyInvoice);
  if (!pathologyInvoices.length) return null;

  const scopePrescriptionId = toPositiveScopeId(
    scope.prescriptionId ?? scope.prescription_id
  );
  const scopeAppointmentId = toPositiveScopeId(
    scope.appointmentId ?? scope.appointment_id
  );

  const labTestIdSet = new Set(
    reportList
      .map(
        (report) =>
          report?.labTestId ??
          report?.lab_test_id ??
          report?.testId ??
          report?.test_id ??
          null
      )
      .filter((id) => id != null && id !== "")
      .map((id) => String(id))
  );

  const reportIdSet = new Set(
    reportList
      .map((report) => report?.reportId ?? report?.report_id ?? report?.id ?? null)
      .filter((id) => id != null && id !== "")
      .map((id) => String(id))
  );

  const normalizedDateKey =
    dateKey && dateKey !== "undated" && dayjs(dateKey).isValid()
      ? dayjs(dateKey).format("YYYY-MM-DD")
      : null;

  const scored = pathologyInvoices
    .map((invoice) => {
      let score = 0;
      const invoiceDateKey = getInvoiceDateKey(invoice);
      if (normalizedDateKey && invoiceDateKey === normalizedDateKey) {
        score += 20;
      }

      const invPresc = getInvoicePrescriptionId(invoice);
      const invAppt = getInvoiceAppointmentId(invoice);
      if (scopePrescriptionId && invPresc === scopePrescriptionId) score += 100;
      if (scopeAppointmentId && invAppt === scopeAppointmentId) score += 90;

      const invoiceLabTestIds = extractLabTestIds(invoice).map((id) => String(id));
      if (invoiceLabTestIds.some((id) => labTestIdSet.has(id))) {
        score += 10;
      }

      const invoiceReportIds = [
        invoice.reportId,
        invoice.report_id,
        invoice.pathLabReportId,
        ...(Array.isArray(invoice.reportIds) ? invoice.reportIds : []),
      ]
        .filter((id) => id != null && id !== "")
        .map((id) => String(id));
      if (invoiceReportIds.some((id) => reportIdSet.has(id))) {
        score += 8;
      }

      return {
        invoice,
        score,
        timestamp: getInvoiceSortTimestamp(invoice),
      };
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return b.timestamp - a.timestamp;
    });

  if (scored[0]?.score > 0) {
    return scored[0].invoice;
  }

  if (scopePrescriptionId || scopeAppointmentId) {
    return null;
  }

  return scored[0]?.invoice || null;
};

/**
 * Print pathology invoice for a prescription/appointment-scoped report group
 * (not all lab tests on the same calendar day).
 */
export const printPathologyLabInvoiceForDayReports = async (
  reports = [],
  {
    user = null,
    patient = null,
    dateKey = null,
    patientsLookup = {},
    prescriptionId = null,
    appointmentId = null,
  } = {}
) => {
  const list = Array.isArray(reports) ? reports.filter(Boolean) : [];
  if (!list.length) {
    throw new Error("No lab reports found for this visit");
  }

  const scopePrescriptionId =
    toPositiveScopeId(prescriptionId) ?? getRecordPrescriptionId(list[0]);
  const scopeAppointmentId =
    toPositiveScopeId(appointmentId) ?? getRecordAppointmentId(list[0]);

  const scopedList = filterReportsByVisitScope(list, {
    prescriptionId: scopePrescriptionId,
    appointmentId: scopeAppointmentId,
  });
  const printList = scopedList.length ? scopedList : list;

  const patientId =
    patient?.id ??
    patient?.patientId ??
    printList[0]?.patientId ??
    printList[0]?.patient_id ??
    null;

  if (patientId == null || patientId === "") {
    throw new Error("Patient ID is required to print pathology invoice");
  }

  const invoice = await findPathologyInvoiceForDayReports(
    patientId,
    printList,
    dateKey,
    {
      prescriptionId: scopePrescriptionId,
      appointmentId: scopeAppointmentId,
    }
  );

  if (!invoice) {
    throw new Error("No pathology invoice found for this visit's lab tests");
  }

  const doctorNamedReport = list.find((report) =>
    hasText(report?.doctorName || report?.assignedByName)
  );
  const doctorName =
    doctorNamedReport?.doctorName ||
    doctorNamedReport?.assignedByName ||
    invoice.doctorName ||
    invoice.doctor_name ||
    null;

  const patientName =
    patient?.name ||
    list[0]?.patientName ||
    invoice.patientName ||
    invoice.toName ||
    invoice.to_name ||
    null;

  const enrichedInvoice = {
    ...invoice,
    patientId: getInvoicePatientId(invoice) || patientId,
    patient_id: invoice.patient_id || patientId,
    patientName: invoice.patientName || patientName,
    toName: invoice.toName || invoice.to_name || patientName,
    doctorName: invoice.doctorName || invoice.doctor_name || doctorName,
    doctor_name: invoice.doctor_name || invoice.doctorName || doctorName,
  };

  const dayLabTestIds = Array.from(
    new Set(
      printList
        .map(
          (report) =>
            report?.labTestId ??
            report?.lab_test_id ??
            report?.testId ??
            report?.test_id ??
            null
        )
        .filter((id) => id != null && id !== "")
    )
  );
  if (dayLabTestIds.length && extractLabTestIds(enrichedInvoice).length === 0) {
    enrichedInvoice.labTestIds = dayLabTestIds;
  }
  if (scopePrescriptionId) {
    enrichedInvoice.prescriptionId =
      enrichedInvoice.prescriptionId ??
      enrichedInvoice.prescription_id ??
      scopePrescriptionId;
  }
  if (scopeAppointmentId) {
    enrichedInvoice.appointmentId =
      enrichedInvoice.appointmentId ??
      enrichedInvoice.appointment_id ??
      scopeAppointmentId;
  }

  const lookup = { ...(patientsLookup || {}) };
  if (patient) {
    lookup[patientId] = patient;
    lookup[String(patientId)] = patient;
  }
  if (!lookup[patientId] && !lookup[String(patientId)]) {
    try {
      const fetchedPatient = await getPatientById(patientId);
      if (fetchedPatient) {
        lookup[patientId] = fetchedPatient;
        lookup[String(patientId)] = fetchedPatient;
      }
    } catch (error) {
      console.warn(
        "[invoiceReprintUtils] Could not load patient for day pathology invoice reprint:",
        error
      );
    }
  }
  if (!lookup[patientId] && !lookup[String(patientId)]) {
    lookup[patientId] = {
      id: patientId,
      name: patientName || "Patient",
      gender: patient?.gender || list[0]?.gender || "",
      dob: patient?.dob || list[0]?.dob || "",
      age: patient?.age || list[0]?.age || "",
      mobile: patient?.mobile || patient?.phone || list[0]?.mobile || "",
      address: patient?.address || list[0]?.address || "",
    };
    lookup[String(patientId)] = lookup[patientId];
  }

  const preferredPathologyTests = [];
  const seenNames = new Set();
  printList.forEach((report) => {
    const testName = String(
      report?.testName ||
        report?.resolvedTestName ||
        report?.labTestName ||
        report?.test_name ||
        ""
    ).trim();
    if (!testName) return;
    const key = testName.toLowerCase();
    if (seenNames.has(key)) return;
    seenNames.add(key);
    preferredPathologyTests.push({
      testName,
      amount:
        Number(
          report?.amount ?? report?.price ?? report?.rate ?? report?.testAmount ?? 0
        ) || 0,
      labTestId:
        report?.labTestId ??
        report?.lab_test_id ??
        report?.testId ??
        report?.test_id ??
        null,
    });
  });

  await printInvoiceFromTable(enrichedInvoice, {
    user,
    patientsLookup: lookup,
    preferredPathologyTests:
      preferredPathologyTests.length > 0 ? preferredPathologyTests : null,
  });

  return invoice;
};

export default printInvoiceFromTable;
